import { FC, useState } from "react"
import { Formik } from "formik"
import { object, string } from "yup"
import { SubscriptionConfigDetails } from "../../../types"
import ManageSubscriptionConfigFormContent from "../../../common/ui/config-admin/config-subscription/manage/form-content"
import Tabs from "../../../common/ui/tabs"
import ConfigEditHeader from "../../../common/ui/config-admin/config-edit-header"
import {
  useDeleteSubscriptionConfigMutation,
  useFetchSubscriptionConfigDetailsQuery,
  useUpdateOrCreateSubscriptionConfigMutation
} from "../../../redux/queries"
import { ConfigEditPageSkeleton } from "../../../common/ui/skeletons"
import { useConfigEditPage } from "../use-config-edit-page"
import { buildPageTitle } from "../utils"
import DefaultConfig from "../../../common/ui/config-admin/config-subscription/manage/default-config.json"

const TABS = [
  { id: "features", label: "Features" },
  { id: "defaults", label: "Defaults" },
  { id: "field-overrides", label: "Field Overrides" }
]

const SubscriptionSchema = object({
  name: string().required().min(1),
  enabled: string().required(),
  brand_id: string().required().min(1),
  config: object().required()
})

const SubscriptionEditPage: FC = () => {
  const [hasChanges, setHasChanges] = useState(false)

  const [updateOrCreate, saveResult] =
    useUpdateOrCreateSubscriptionConfigMutation()
  const [deleteSubscription, deleteResult] =
    useDeleteSubscriptionConfigMutation()

  const {
    id,
    isCreate,
    formRef,
    setFormField,
    tab,
    setTab,
    brands,
    navigateBack
  } = useConfigEditPage<SubscriptionConfigDetails>({
    tabs: TABS,
    tab: "subscription",
    label: "subscription config",
    saveResult,
    deleteResult
  })

  const { data: subscriptionData, isLoading } =
    useFetchSubscriptionConfigDetailsQuery(
      { id: id ?? "" },
      { skip: isCreate || !id }
    )

  const raw = subscriptionData ?? {
    name: "",
    id: "",
    enabled: "0",
    brand_id: "",
    created: "",
    is_default: "0",
    config: DefaultConfig
  }

  const initialValues: SubscriptionConfigDetails = {
    ...raw,
    name: raw.name || "",
    is_default: raw.is_default || "0",
    config: raw.config ?? DefaultConfig
  }

  const setDetailsField = (field: string, value: any) => {
    const currentConfig = formRef.current?.values.config || {}
    const newConfig = {
      ...currentConfig,
      [field]: value
    }
    formRef.current?.setFieldValue("config", newConfig, false)
    setHasChanges(true)
  }

  const onSubmit = () => {
    if (formRef.current?.values) updateOrCreate(formRef.current.values)
  }

  const onDelete = () => {
    if (subscriptionData?.id) deleteSubscription(subscriptionData.id)
  }

  if (!isCreate && isLoading) {
    return <ConfigEditPageSkeleton tabCount={4} />
  }

  const brandName = brands?.find(b => b.id === raw.brand_id)?.name
  const pageTitle = buildPageTitle(isCreate, "Subscription Config", brandName)

  return (
    <div className="min-h-screen bg-gray-50">
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        innerRef={formRef}
        validationSchema={SubscriptionSchema}
        enableReinitialize
      >
        {({ values, isValid, dirty }) => (
          <>
            <ConfigEditHeader
              backLabel="Subscription Configs"
              namePlaceholder="Subscription config name"
              name={values.name}
              pageTitle={pageTitle}
              isCreate={isCreate}
              isValid={isValid}
              dirty={dirty || hasChanges}
              isDefault={values.is_default === "1"}
              isEnabled={values.enabled === "1"}
              brands={brands ?? []}
              brandId={values.brand_id}
              onNameChange={val => setFormField("name", val)}
              onDefaultChange={v => setFormField("is_default", v ? "1" : "0")}
              onEnabledChange={v => setFormField("enabled", v ? "1" : "0")}
              onBrandChange={val => setFormField("brand_id", val)}
              onDelete={onDelete}
              onCancel={() => navigateBack(values.brand_id)}
              onSubmit={onSubmit}
              onBack={() => navigateBack(values.brand_id)}
              saving={saveResult.isLoading}
            />

            <Tabs
              currentTab={tab}
              tabs={TABS}
              onChange={setTab}
              className="px-6 bg-white"
            />

            <div className="bg-white">
              <div className="max-w-2xl py-6 px-6">
                <ManageSubscriptionConfigFormContent
                  values={values}
                  tab={tab}
                  setDetailsField={setDetailsField}
                />
              </div>
            </div>
          </>
        )}
      </Formik>
    </div>
  )
}

export default SubscriptionEditPage
